// The FRAGO (fragmentary order) card's own panel: one Command-family card accepts plain English instead
// of a map gesture (spec §6 "free-text FRAGO ... a convenience on top of the deck, not a replacement";
// ai-patterns.md #4 "parse-and-confirm"). It never issues anything directly - the parsed result always
// lands as pending cards the player confirms or edits first, exactly like an advisor's play.

import { useState } from 'react';
import { card, FAMILY_META } from '../../sim/deck';
import type { Graphic, Target, Unit } from '../../sim/types';
import type { Grid } from '../../sim/grid';
import { useStore } from '../../state/store';
import * as audit from '../../ai/audit';
import { callLLM } from '../../ai/client';
import { FragoSchema } from '../../ai/schemas';
import { validatePlay, type ValidOrder } from '../../ai/validate';
import { buildFragoPrompt, type FragoContext } from '../../ai/feedPrompts';
import { parseFragoDeterministic } from '../../ai/fragoParse';
import { gridRef } from '../reports';
import { Chip, ShapeTile } from '../cards/ui';

const PLACEHOLDER = '2nd platoon screen PL RED, arty on NAI 2';

type Phase = 'compose' | 'review';

interface Rejected { order: unknown; reason: string }

function timeoutMs(): number {
  const raw = Number(import.meta.env.VITE_LLM_TIMEOUT_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : 6000;
}

function targetSummary(target: Target, units: Unit[], graphics: Graphic[], grid: Grid | null): string {
  switch (target.kind) {
    case 'point':
      return target.cell !== undefined ? `vic ${gridRef(target.cell, grid)}` : 'a point';
    case 'graphic': {
      const g = graphics.find((x) => x.id === target.graphicId);
      return g ? g.name : 'a control graphic';
    }
    case 'unit': {
      const u = units.find((x) => x.id === target.unitId);
      return u ? u.callsign : 'a unit';
    }
    case 'contact':
      return target.unitId !== undefined ? `reported contact #${target.unitId}` : 'a contact';
    default:
      return '—';
  }
}

export function FragoCard({ issuer, onClose }: { issuer: Unit; onClose: () => void }) {
  const sim = useStore((s) => s.sim);
  const snapshot = useStore((s) => s.snapshot);
  const grid = useStore((s) => s.grid);
  const scenario = useStore((s) => s.scenario);

  const [text, setText] = useState('');
  const [phase, setPhase] = useState<Phase>('compose');
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState<ValidOrder[]>([]);
  const [rejected, setRejected] = useState<Rejected[]>([]);
  const [unparsed, setUnparsed] = useState<string | undefined>(undefined);
  const [source, setSource] = useState<'llm' | 'template'>('template');

  async function submit(): Promise<void> {
    if (!snapshot || !grid || !scenario || text.trim().length === 0 || busy) return;
    setBusy(true);
    const units = snapshot.units.filter((u) => u.side === 0 && u.alive);
    const graphics = scenario.graphics;
    const ctx: FragoContext = { units, graphics, grid };
    const { system, user } = buildFragoPrompt(text, ctx);
    const res = await callLLM(
      {
        model: 'sonnet', system, user, schema: FragoSchema, timeoutMs: timeoutMs(),
        purpose: 'frago', auditRef: text.trim().slice(0, 40),
      },
      () => parseFragoDeterministic(text, ctx),
    );
    // `validatePlay` wants a full play (name/rationale/cited_event_ids); the FRAGO schema only carries
    // `orders`/`unparsed`, so it is wrapped rather than widening `validatePlay`'s own contract.
    const play = { name: 'FRAGO', rationale: text, cited_event_ids: [], orders: res.value.orders };
    const { orders: valid, rejected: bad } = validatePlay(play, snapshot.units, 0, grid, graphics);
    // The parse is wrapped in a play before validation, so its audit row is annotated by hand
    // (src/ai/audit.ts): what the parser proposed, what legality kept, and why the rest went.
    audit.note(res.auditId, {
      ordersKept: valid.length,
      ordersRejected: bad.map((r) => r.reason),
    });
    setOrders(valid);
    setRejected(bad);
    setUnparsed(res.value.unparsed);
    setSource(res.source);
    setBusy(false);
    setPhase('review');
  }

  function removeOrder(i: number): void {
    setOrders((prev) => prev.filter((_, idx) => idx !== i));
  }

  function confirm(): void {
    if (sim && orders.length > 0) {
      useStore.getState().noteInteraction();
      sim.issuePlay('frago', orders);
    }
    onClose();
  }

  return (
    // Opaque, not the 60% it wore inside the deck's list: it now floats over the map (src/ui/deck/Deck.tsx
    // `FragoPanel`), and a composer you can read the terrain through is not a composer.
    <div
      data-testid="frago-card"
      className="space-y-2 rounded-sm border bg-slate-900/97 p-2 backdrop-blur-sm"
      style={{ borderColor: 'var(--color-command)' }}
    >
      <div className="flex items-center justify-between">
        <span className="mono text-[10px] uppercase tracking-[0.15em] text-fog">FRAGO — {issuer.callsign}</span>
        <Chip onClick={onClose} testId="frago-close" title="Back to the order deck">close</Chip>
      </div>

      {phase === 'compose' && (
        <div className="space-y-2">
          <textarea
            data-testid="frago-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={5}
            className="mono w-full resize-none rounded-sm border border-slate-700 bg-slate-800 p-2 text-[11px] leading-snug text-paper placeholder:text-slate-700"
          />
          <div className="flex items-center justify-between">
            <span className="mono text-[9px] text-fog">Unit references: callsigns ("A11") or descriptions ("2nd platoon of A company")</span>
          </div>
          <div className="flex justify-end">
            <Chip onClick={submit} disabled={busy || text.trim().length === 0} testId="frago-submit">
              {busy ? 'parsing…' : 'parse order'}
            </Chip>
          </div>
        </div>
      )}

      {phase === 'review' && snapshot && grid && scenario && (
        <div className="space-y-1.5">
          <div className="mono flex items-center gap-1 text-[9px]">
            <span className="uppercase tracking-[0.15em] text-fog">parsed order{orders.length === 1 ? '' : 's'}</span>
            {source === 'template' && (
              <Chip title="The model was unavailable; this used the deterministic fallback parser.">template</Chip>
            )}
          </div>

          {orders.length === 0 && rejected.length === 0 && !unparsed && (
            <p className="mono text-[10px] text-fog">Nothing came back. Try rephrasing, or use the deck directly.</p>
          )}

          {orders.map((o, i) => {
            const def = card(o.card);
            const meta = FAMILY_META[def.family];
            const unit = snapshot.units.find((u) => u.id === o.unitId);
            return (
              <div
                key={`${o.unitId}-${o.card}-${i}`}
                data-testid="frago-order"
                className="flex items-center gap-2 rounded-sm border border-slate-700 bg-slate-800 px-2 py-1.5"
              >
                <ShapeTile meta={meta} size={20} solid />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] text-paper">{unit?.callsign ?? `unit ${o.unitId}`} — {def.label}</span>
                  <span className="mono block truncate text-[10px] text-fog">{targetSummary(o.target, snapshot.units, scenario.graphics, grid)}</span>
                </span>
                <button
                  type="button"
                  data-testid="frago-remove"
                  onClick={() => removeOrder(i)}
                  title="Remove this order before confirming"
                  className="mono px-1 text-[12px] text-fog hover:text-hostile"
                >
                  ✕
                </button>
              </div>
            );
          })}

          {rejected.map((r, i) => (
            <div key={`rej-${i}`} data-testid="frago-rejected" className="rounded-sm border border-dashed border-slate-700 px-2 py-1.5 opacity-70">
              <span className="mono block text-[10px] text-hostile">dropped: {r.reason}</span>
            </div>
          ))}

          {unparsed && (
            <div data-testid="frago-unparsed" className="rounded-sm border border-dashed border-warn/40 px-2 py-1.5">
              <span className="mono block text-[10px] text-warn">could not parse: {unparsed}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <Chip onClick={() => setPhase('compose')} testId="frago-edit-text">edit text</Chip>
            <Chip onClick={confirm} active disabled={orders.length === 0} testId="frago-confirm">
              confirm ({orders.length})
            </Chip>
          </div>
        </div>
      )}
    </div>
  );
}
